import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { ExternalLink, Check, Loader2, KeyRound } from "lucide-react";
import { YouTubeIcon } from "./YouTubeIcon";
import { t } from "../i18n";

const STEPS = [
  { t: t("Criar/selecionar um projeto"), d: t("Crie um projeto novo (qualquer nome serve) ou use um que já tenha."), btn: t("Abrir Google Cloud Console"), url: "https://console.cloud.google.com/projectcreate" },
  { t: t("Ativar a YouTube Data API v3"), d: t("Clique em Ativar na página que abrir."), btn: t("Ativar a API"), url: "https://console.cloud.google.com/apis/library/youtube.googleapis.com" },
  { t: t("Criar a chave"), d: t("Clique em Criar credenciais → Chave de API, e copie a chave gerada."), btn: t("Ir para Credenciais"), url: "https://console.cloud.google.com/apis/credentials" },
];

export function YouTubeKeyWizard({ open, onOpenChange, onSaved }) {
  const [done, setDone] = useState([false, false, false]);
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {ok, msg}

  const save = async () => {
    if (!key.trim()) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/youtube/key", { api_key: key.trim() });
      setResult({ ok: true, msg: t("Chave validada e salva! Ela vale para todos os seus overlays.") });
      toast.success(t("Chave do YouTube conectada"));
      onSaved?.(data);
      setKey("");
    } catch (e) {
      setResult({ ok: false, msg: e.response?.data?.detail || t("Falha ao validar a chave. Tente novamente.") });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="yt-wizard">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <YouTubeIcon /> {t("Conectar seu YouTube")}
          </DialogTitle>
          <DialogDescription>
            {t("Uma chave gratuita do Google Cloud, configurada uma única vez, vale para todos os seus overlays. Se você já tem a chave, pule direto para o passo 4.")}
          </DialogDescription>
        </DialogHeader>

        {STEPS.map((s, i) => (
          <div key={i} className={`border rounded-md p-3 ${done[i] ? "border-emerald-500/40 bg-emerald-500/5" : "border-border/60"}`} data-testid={`yt-wizard-step-${i + 1}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-semibold">
                  <span className="text-primary font-mono mr-1.5">{i + 1}.</span>{s.t}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">{s.d}</p>
              </div>
              <Button size="sm" variant="secondary" className="rounded-sm h-7 text-[11px] flex-shrink-0" onClick={() => window.open(s.url, "_blank")} data-testid={`yt-wizard-link-${i + 1}`}>
                <ExternalLink className="h-3 w-3 mr-1" /> {s.btn}
              </Button>
            </div>
            <label className="flex items-center gap-2 mt-2 text-[11px] text-muted-foreground cursor-pointer">
              <input type="checkbox" checked={done[i]} onChange={(e) => setDone((p) => p.map((v, j) => (j === i ? e.target.checked : v)))} className="accent-primary h-3.5 w-3.5" data-testid={`yt-wizard-check-${i + 1}`} />
              {t("concluí este passo")}
            </label>
          </div>
        ))}

        <div className="border border-primary/40 rounded-md p-3 bg-primary/5" data-testid="yt-wizard-step-4">
          <p className="text-sm font-semibold flex items-center gap-1.5">
            <span className="text-primary font-mono">4.</span> {t("Colar e validar")}
            <KeyRound className="h-3.5 w-3.5 text-primary" />
          </p>
          <div className="flex gap-2 mt-2">
            <input
              value={key} onChange={(e) => setKey(e.target.value)} placeholder="AIzaSy..."
              onKeyDown={(e) => { if (e.key === "Enter") save(); }}
              className="flex-1 h-9 bg-input border border-border rounded-sm px-2 text-xs font-mono outline-none focus:border-primary"
              data-testid="yt-wizard-key-input"
            />
            <Button onClick={save} disabled={busy || !key.trim()} size="sm" className="rounded-sm h-9" data-testid="yt-wizard-save-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Salvar")}
            </Button>
          </div>
          {result && (
            <p className={`text-xs mt-2 flex items-start gap-1.5 ${result.ok ? "text-emerald-400" : "text-red-400"}`} data-testid="yt-wizard-result">
              {result.ok && <Check className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />}
              {result.msg}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
